import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function CaseStudies() {
  const caseStudies = [
    {
      company: 'Digital Growth Agency',
      industry: 'Marketing Agency',
      size: '25-50 employees',
      logo: '🚀',
      challenge: 'Managing 15 client accounts with only 2 social media managers, leading to burnout and inconsistent posting schedules.',
      solution: 'Implemented MandaPost to automate content planning, scheduling, and analytics across all client accounts.',
      results: [
        { metric: '65%', label: 'Time Savings', icon: 'ri-time-line' },
        { metric: '$42K', label: 'Annual Savings', icon: 'ri-money-dollar-circle-line' },
        { metric: '340%', label: 'Client Growth', icon: 'ri-line-chart-line' },
        { metric: '99%', label: 'Client Retention', icon: 'ri-heart-line' },
      ],
      testimonial: {
        quote: "MandaPost transformed our agency. We went from barely keeping up with 15 clients to confidently managing 45 clients with the same team size. The ROI was evident within the first month.",
        author: 'Sarah Mitchell',
        role: 'CEO, Digital Growth Agency',
      },
      color: 'from-blue-500 to-purple-500',
    },
    {
      company: 'Urban Threads Boutique',
      industry: 'E-commerce Fashion',
      size: '10-25 employees',
      logo: '👗',
      challenge: 'Low engagement rates and inconsistent brand presence across Instagram, TikTok, and Pinterest led to stagnant sales.',
      solution: 'Used MandaPost\'s AI to create on-brand content calendars, optimize posting times, and track competitor strategies.',
      results: [
        { metric: '285%', label: 'Engagement Increase', icon: 'ri-heart-3-line' },
        { metric: '180%', label: 'Sales Growth', icon: 'ri-shopping-cart-line' },
        { metric: '12K', label: 'New Followers', icon: 'ri-user-add-line' },
        { metric: '4.2x', label: 'ROI', icon: 'ri-bar-chart-box-line' },
      ],
      testimonial: {
        quote: "We saw results immediately. The AI knew exactly when our audience was active and what content resonated. Our Instagram went from 3K to 15K followers in 6 months, and sales followed.",
        author: 'Jessica Chen',
        role: 'Owner, Urban Threads Boutique',
      },
      color: 'from-pink-500 to-rose-500',
    },
    {
      company: 'TechStart Innovations',
      industry: 'B2B SaaS',
      size: '50-100 employees',
      logo: '💻',
      challenge: 'Complex product required educational content strategy across LinkedIn, Twitter, and YouTube but lacked coordination.',
      solution: 'Centralized content planning with MandaPost, using AI to repurpose content across platforms and track thought leadership impact.',
      results: [
        { metric: '425%', label: 'LinkedIn Reach', icon: 'ri-linkedin-line' },
        { metric: '$1.2M', label: 'Pipeline Generated', icon: 'ri-funds-line' },
        { metric: '89%', label: 'Content Efficiency', icon: 'ri-speed-line' },
        { metric: '250+', label: 'Quality Leads/Mo', icon: 'ri-user-star-line' },
      ],
      testimonial: {
        quote: "MandaPost helped us establish thought leadership in our niche. The AI content suggestions aligned perfectly with our expertise, and the analytics proved the ROI to our board.",
        author: 'Michael Torres',
        role: 'VP of Marketing, TechStart Innovations',
      },
      color: 'from-cyan-500 to-blue-500',
    },
    {
      company: 'Wellness Haven Spa',
      industry: 'Health & Wellness',
      size: '5-10 employees',
      logo: '🧘',
      challenge: 'Owner spent 15+ hours weekly on social media, taking time away from clients and business operations.',
      solution: 'Automated posting schedule, AI-generated wellness tips and quotes, and local SEO optimization through Google My Business integration.',
      results: [
        { metric: '92%', label: 'Time Saved', icon: 'ri-hourglass-line' },
        { metric: '156%', label: 'Booking Increase', icon: 'ri-calendar-check-line' },
        { metric: '$65K', label: 'Revenue Growth', icon: 'ri-money-dollar-box-line' },
        { metric: '4.9★', label: 'Average Rating', icon: 'ri-star-fill' },
      ],
      testimonial: {
        quote: "I got my life back! MandaPost handles all my social media while I focus on clients. Bookings increased so much I hired two more therapists. Best investment I've ever made.",
        author: 'Amanda Rodriguez',
        role: 'Owner, Wellness Haven Spa',
      },
      color: 'from-green-500 to-emerald-500',
    },
    {
      company: 'Velocity Sports Apparel',
      industry: 'Sports & Fitness',
      size: '25-50 employees',
      logo: '⚡',
      challenge: 'Struggling to compete with major brands on social media, leading to low brand awareness and DTC sales.',
      solution: 'Leveraged MandaPost\'s competitor tracking, trend analysis, and AI content creation to punch above their weight class.',
      results: [
        { metric: '520%', label: 'Brand Mentions', icon: 'ri-chat-voice-line' },
        { metric: '380%', label: 'DTC Revenue', icon: 'ri-shopping-bag-line' },
        { metric: '28K', label: 'UGC Posts', icon: 'ri-user-shared-line' },
        { metric: '#1', label: 'Category Ranking', icon: 'ri-trophy-line' },
      ],
      testimonial: {
        quote: "We went from unknown to industry leader in 18 months. MandaPost's competitor insights showed us exactly where to compete and win. Our social proof exploded.",
        author: 'David Park',
        role: 'CMO, Velocity Sports Apparel',
      },
      color: 'from-orange-500 to-red-500',
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#e4f4ff]/20 via-white to-[#b3d9ff]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-award-line mr-2"></i>
            Success Stories
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-[#22577A] mb-6">
            Real Results from
            <span className="block text-[#38a3a5]">Real Businesses</span>
          </h1>

          <p className="text-xl text-gray-700 mb-8">
            See how companies of all sizes are scaling their social media with MandaPost
          </p>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-200">
              <div className="text-3xl font-bold text-[#22577A]">50,000+</div>
              <p className="text-sm text-gray-600">Happy Customers</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-200">
              <div className="text-3xl font-bold text-[#22577A]">285%</div>
              <p className="text-sm text-gray-600">Avg. Engagement Boost</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-200">
              <div className="text-3xl font-bold text-[#22577A]">65%</div>
              <p className="text-sm text-gray-600">Time Saved</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-200">
              <div className="text-3xl font-bold text-[#22577A]">4.8x</div>
              <p className="text-sm text-gray-600">Average ROI</p>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white to-[#e4f4ff]/10 rounded-3xl overflow-hidden shadow-2xl border-2 border-gray-200"
              >
                {/* Header */}
                <div className={`bg-gradient-to-r ${study.color} p-8 text-white`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-4xl">
                        {study.logo}
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold">{study.company}</h2>
                        <p className="text-white/90">{study.industry} • {study.size}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  {/* Challenge & Solution */}
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h3 className="text-xl font-bold text-[#22577A] mb-3 flex items-center gap-2">
                        <i className="ri-error-warning-line text-red-500"></i>
                        The Challenge
                      </h3>
                      <p className="text-gray-700 leading-relaxed">{study.challenge}</p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#22577A] mb-3 flex items-center gap-2">
                        <i className="ri-lightbulb-line text-yellow-500"></i>
                        The Solution
                      </h3>
                      <p className="text-gray-700 leading-relaxed">{study.solution}</p>
                    </div>
                  </div>

                  {/* Results Grid */}
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-[#22577A] mb-6 flex items-center gap-2">
                      <i className="ri-line-chart-line text-green-500"></i>
                      The Results
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {study.results.map((result, idx) => (
                        <div
                          key={idx}
                          className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg transition-all text-center"
                        >
                          <i className={`${result.icon} text-4xl text-[#22577A] mb-2`}></i>
                          <div className="text-4xl font-bold text-[#38a3a5] mb-1">{result.metric}</div>
                          <p className="text-sm text-gray-600">{result.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Testimonial */}
                  <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-white rounded-2xl p-8 border-2 border-[#b3d9ff]">
                    <div className="flex items-center gap-2 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                      ))}
                    </div>
                    <p className="text-xl text-gray-800 mb-4 italic">"{study.testimonial.quote}"</p>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {study.testimonial.author.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-[#22577A]">{study.testimonial.author}</p>
                        <p className="text-gray-600 text-sm">{study.testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join 50,000+ marketers who are scaling their social media with MandaPost
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/signup"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-lg font-semibold text-lg hover:shadow-xl hover:scale-105 transition-all"
            >
              <i className="ri-rocket-line"></i>
              Start Your Free Trial
            </a>
            <a
              href="/tools/roi-calculator"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white/10 hover:scale-105 transition-all"
            >
              <i className="ri-calculator-line"></i>
              Calculate Your ROI
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
